/**
 * Global status chip for background community media uploads.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  ErrorOutline,
} from '@mui/icons-material';
import {
  dismissCommunityUpload,
  getCommunityUploadJobs,
  retryCommunityUpload,
  subscribeCommunityUploads,
} from '../../services/communityPostUploadQueue';

function labelFor(job) {
  if (job.status === 'queued') return 'Waiting to upload…';
  if (job.status === 'preparing') return 'Finishing video adjustments…';
  if (job.status === 'uploading') {
    const kind = job.videoFile ? 'video' : job.imageFile ? 'photo' : 'post';
    return `Uploading ${kind}… ${Math.max(0, Math.min(100, job.progress || 0))}%`;
  }
  if (job.status === 'posting') return 'Publishing post…';
  if (job.status === 'done') return 'Posted to Community';
  if (job.status === 'error') return job.error || 'Upload failed';
  return 'Uploading…';
}

export default function CommunityUploadStatusBar() {
  const [jobs, setJobs] = useState(() => getCommunityUploadJobs());

  useEffect(() => subscribeCommunityUploads((_event, snapshot) => {
    setJobs(snapshot);
  }), []);

  const visible = jobs.filter((j) =>
    ['queued', 'preparing', 'uploading', 'posting', 'done', 'error'].includes(j.status)
  );
  if (!visible.length) return null;

  const primary = visible.find((j) =>
    ['preparing', 'uploading', 'posting', 'queued'].includes(j.status)
  ) || visible[0];

  return (
    <Box
      sx={{
        position: 'fixed',
        right: { xs: 12, sm: 24 },
        bottom: { xs: 12, sm: 24 },
        zIndex: (t) => t.zIndex.snackbar,
        maxWidth: 360,
        width: 'calc(100% - 24px)',
      }}
      data-testid="community-upload-status"
    >
      <Paper elevation={6} sx={{ overflow: 'hidden', borderRadius: 2 }}>
        {(primary.status === 'uploading' ||
          primary.status === 'posting' ||
          primary.status === 'queued' ||
          primary.status === 'preparing') && (
          <LinearProgress
            variant={primary.status === 'uploading' ? 'determinate' : 'indeterminate'}
            value={primary.progress || 0}
          />
        )}
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ p: 1.5 }}>
          {primary.status === 'error' ? (
            <ErrorOutline color="error" fontSize="small" sx={{ mt: 0.25 }} />
          ) : primary.status === 'done' ? (
            <CheckCircle color="success" fontSize="small" sx={{ mt: 0.25 }} />
          ) : (
            <CloudUpload color="primary" fontSize="small" sx={{ mt: 0.25 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {labelFor(primary)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              You can keep browsing while this finishes.
            </Typography>
            {primary.status === 'error' && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => retryCommunityUpload(primary.tempId)}
                  sx={{ textTransform: 'none' }}
                >
                  Retry
                </Button>
                <Button
                  size="small"
                  onClick={() => dismissCommunityUpload(primary.tempId)}
                  sx={{ textTransform: 'none' }}
                >
                  Dismiss
                </Button>
              </Stack>
            )}
          </Box>
        </Stack>
        {primary.status === 'error' && primary.error && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            {primary.error}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
